import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import useApi from "_api/index";
import CustomizeDialog from "components/Car/CustomizeDialog";
import CustomizeDatePicker from "components/DatePicker";
import { GradientButtonOutline } from "components/GradientButton";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useEffect, useState } from "react";
import nationalities from "data/nationalities_th.json";
import OptionProvince from "components/OptionProvince";
import { useCarBrands } from "contexts/CarBrandsContext";
import { getBrandName } from "utils/helpers/brandCar";
import * as yup from "yup";
import AlertToast from "components/AlertToast";

const Attachments = [
  { id: 1, label: "เล่มทะเบียนรถ" },
  { id: 2, label: "บัตรประชาชนของผู้โอน" },
  { id: 3, label: "บัตรประชาชนของผู้รับโอน" },
  { id: 3, label: "หนังสือมอบอำนาจ" },
  { id: 4, label: "ใบคู่มือประกันภัย พ.ร.บ." },
];

const ModalCreateTransferAndReceiptBuy = ({
  isOpen,
  onClose,
  car,
  customer = {},
  order = {},
  type = "buy",
  document = null,
  onSuccess,
}) => {
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [error, setError] = useState({
    open: false,
    status: "info",
    message: "",
  });
  useEffect(() => {
    if (isOpen && customer && car) {
      let valuesFormatted = {
        ...formik.values,
        request_date: new Date(),
        seller_nationality: "ไทย",
        car_code: car ? car.car_code : "",
        license_plate: car ? car.license_plate : "",
        license_province: car ? car.license_province : "",
        car_type: car ? car.car_type : "",
        engine_brand: car ? car.engine_brand : "",
        car_no: car ? car.car_no : "",
        engine_number: car ? car.engine_number : "",
        total_price: car ? car.sell_price ?? car.car_price ?? "" : "",
        buyer_nationality: "ไทย",
      };
      if (type === "buy" || type === "pawn_car") {
        valuesFormatted = {
          ...valuesFormatted,
          seller_name: customer ? customer.customer_name : "",
          seller_phone: customer ? customer.customer_phonenumber : "",
          seller_address: customer ? customer.customer_address : "",
        };
      } else if (type === "sell_car") {
        valuesFormatted = {
          ...valuesFormatted,
          buyer_name: customer ? customer.customer_name : "",
          buyer_phone: customer ? customer.customer_phonenumber : "",
          buyer_address: customer ? customer.customer_address : "",
        };
      }
      formik.setValues(valuesFormatted);
    }
    if (isOpen && document) {
      fetchDocument();
    } else if (!isOpen) {
      formik.resetForm();
      setSelectedAttachments([]);
    }
  }, [isOpen, document, car]);

  const fetchDocument = async () => {
    if (!document) return;
    try {
      let response = null;
      if (type === "buy") {
        response = await useApi.buy_car.getTransferAndReceiptBuyByID(
          document.document_id
        );
      } else if (type == "sell_car") {
        response = await useApi.ready_car.getTransferAndReceiptReadyByID(
          document.document_id
        );
      } else if (type == "pawn_car") {
        response = await useApi.pawn_car.getTransferAndReceiptPawnByID(
          document.document_id
        );
      }
      if (response.success) {
        const { data } = response;
        formik.setValues({
          ...data,
        });
        const files = [];
        for (let i = 1; i <= 6; i++) {
          const key = `document_file_${i}`;
          const file = data?.[key];
          if (file != null) {
            files.push(file);
          }
        }
        setSelectedAttachments(files);
      }
    } catch (error) {
      console.error("Error fetching document:", error);
    }
  };

  const { carBrands } = useCarBrands();

  const brandName = useMemo(() => {
    if (!car || !car.car_brand_id) return "ยี่ห้อรถยนต์";
    // const brand = carBrands.find((b) => b.id === car.car_brand_id);
    return getBrandName(car.car_brand_id, carBrands);
  }, [car, carBrands]);

  const generateValidationSchema = () => {
    return yup.object({
      request_date: yup.date().required("กรุณากรอกวันที่ยื่นคำขอ"),
      seller_name: yup.string().required("กรุณากรอกชื่อผู้ขาย"),
      seller_age: yup
        .number()
        .required("กรุณากรอกอายุผู้ขาย")
        .min(18, "อายุต้องไม่น้อยกว่า 18 ปี"),
      seller_nationality: yup.string().required("กรุณากรอกสัญชาติผู้ขาย"),
      seller_phone: yup.string().required("กรุณากรอกโทรศัพท์ผู้ขาย"),
      seller_address: yup.string().required("กรุณากรอกที่อยู่ผู้ขาย"),
      buyer_name: yup.string().required("กรุณากรอกชื่อผู้ซื้อ"),
      buyer_age: yup
        .number()
        .required("กรุณากรอกอายุผู้ซื้อ")
        .min(18, "อายุต้องไม่น้อยกว่า 18 ปี"),
      buyer_nationality: yup.string().required("กรุณากรอกสัญชาติผู้ซื้อ"),
      buyer_phone: yup.string().required("กรุณากรอกโทรศัพท์ผู้ซื้อ"),
      buyer_address: yup.string().required("กรุณากรอกที่อยู่ผู้ซื้อ"),
      total_price: yup
        .number()
        .required("กรุณากรอกยอดเงินทั้งหมด")
        .min(0, "ยอดเงินต้องไม่น้อยกว่า 0"),
    });
  };

  const formik = useFormik({
    initialValues: {
      request_date: new Date(),
      seller_name: "",
      seller_age: "",
      seller_nationality: "ไทย",
      seller_phone: "",
      seller_address: "",
      car_code: "",
      license_plate: "",
      license_province: "",
      car_type: "",
      engine_brand: "",
      car_no: "",
      engine_brand: "",
      engine_number: "",
      buyer_name: "",
      buyer_age: "",
      buyer_nationality: "ไทย",
      buyer_phone: "",
      buyer_address: "",
      total_price: "",
    },
    validationSchema: generateValidationSchema(),
    onSubmit: (values) => {
      const formattedValues = {
        ...values,
        document_file: selectedAttachments,
      };
      // console.log("Form submitted with values:", formattedValues);
      if (type === "buy") {
        handleCreateBuyForm(formattedValues);
      } else if (type === "sell_car") {
        handleCreateReadySellForm(formattedValues);
      } else if (type === "pawn_car") {
        handleCreatePawnForm(formattedValues);
      }
    },
  });

  const handleOnSuccess = (success) => {
    if (success) {
      setError({
        open: true,
        status: "success",
        message: document
          ? "แก้ไขแบบคำขอและรับโอนสำเร็จ"
          : "สร้างแบบคำขอและรับโอนสำเร็จ",
      });
      onSuccess();
      onClose();
    }
  };

  const handleCreateBuyForm = async (values) => {
    try {
      let response = null;
      if (document) {
        response = await useApi.buy_car.updateTransformAndReceiptBuy(
          document?.document_id,
          values
        );
      } else {
        response = await useApi.buy_car.createTransferAndReceiptBuy(
          order?.order_id,
          values
        );
      }
      if (response.success) {
        handleOnSuccess(response.success);
      } else {
        console.error("Failed to update document status:", response.message);
      }
    } catch (error) {
      console.error("Error updating document status:", error);
    }
  };

  const handleCreateReadySellForm = async (values) => {
    try {
      let response = null;
      if (document) {
        response = await useApi.ready_car.updateTransferAndReceiptReadySell(
          document?.document_id,
          values
        );
      } else {
        response = await useApi.ready_car.createTransferAndReceiptReady(
          order?.order_id,
          values
        );
      }
      if (response.success) {
        handleOnSuccess(response.success);
      } else {
        console.error("Failed to update document status:", response.message);
      }
    } catch (error) {
      console.error("Error updating document status:", error);
    }
  };

  const handleCreatePawnForm = async (values) => {
    try {
      let response = null;
      if (document) {
        response = await useApi.pawn_car.updateTransformAndReceiptPawn(
          values,
          document?.document_id
        );
      } else {
        response = await useApi.pawn_car.createTransferAndReceiptPawn(
          order?.order_id,
          values
        );
      }
      if (response.success) {
        handleOnSuccess(response.success);
      } else {
        console.error("Failed to update document status:", response.message);
      }
    } catch (error) {
      console.error("Error updating document status:", error);
    }
  };

  return (
    <>
      <CustomizeDialog
        open={isOpen}
        onClose={onClose}
        title={document ? "แก้ไขแบบคำขอและรับโอน" : "สร้างแบบคำขอและรับโอน"}
        fullWidth
        maxWidth="md"
        onSave={formik.handleSubmit}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: 1,
              width: "100%",
              "& > button": {
                height: "40px",
                cursor: "default",
                pointerEvents: "none",
                background: "rgba(0, 187, 159, 0.2)",
                "&:hover": {
                  background: "transparent",
                },
                boxShadow: "none",
              },
            }}
          >
            <GradientButtonOutline>
              {car ? car.car_type : "รถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car ? brandName : "ยี่ห้อรถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car
                ? car.license_plate + " " + car.license_province
                : "ทะเบียนรถ"}
            </GradientButtonOutline>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              แบบคำขอและรับโอน
            </Typography>
            <Grid container spacing={2} px={1}>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={2}>
                    <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                      วันที่ยื่นคำขอ
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <CustomizeDatePicker
                      value={formik.values["request_date"]}
                      onChange={(newValue) => {
                        formik.setFieldValue("request_date", newValue);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                      error={
                        formik.touched.request_date &&
                        Boolean(formik.errors.request_date)
                      }
                      helperText={
                        formik.touched.request_date &&
                        formik.errors.request_date
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลผู้โอน
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ชื่อผู้ขาย",
                    name: "seller_name",
                    type: "text",
                    placeholder: "กรุณากรอกชื่อผู้ขาย",
                  }}
                  formik={formik}
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  อายุ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  name="seller_age"
                  placeholder="กรุณากรอกอายุ"
                  value={formik.values["seller_age"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  InputProps={{ inputProps: { min: 18 } }}
                  error={
                    formik.touched.seller_age &&
                    Boolean(formik.errors.seller_age)
                  }
                  helperText={
                    formik.touched.seller_age && formik.errors.seller_age
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  สัญชาติ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={nationalities}
                  getOptionLabel={(option) => option.nationality}
                  value={
                    nationalities.find(
                      (n) => n.nationality === formik.values.seller_nationality
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "seller_nationality",
                      newValue?.nationality || ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="กรุณากรอกสัญชาติ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                      error={
                        formik.touched.seller_nationality &&
                        Boolean(formik.errors.seller_nationality)
                      }
                      helperText={
                        formik.touched.seller_nationality &&
                        formik.errors.seller_nationality
                      }
                    />
                  )}
                  disableClearable
                />
              </Grid>

              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "โทรศัพท์",
                    name: "seller_phone",
                    type: "text",
                    placeholder: "กรุณากรอกโทรศัพท์",
                  }}
                  formik={formik}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ที่อยู่",
                    name: "seller_address",
                    type: "text",
                    placeholder: "กรุณากรอกที่อยู่",
                  }}
                  formik={formik}
                />
              </Grid>
            </Grid>
            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลรถ
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "รหัสรถ",
                    name: "car_code",
                    type: "text",
                    placeholder: "กรุณากรอกรหัสรถ",
                  }}
                  formik={formik}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ทะเบียน
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="text"
                  name="license_plate"
                  placeholder="กรุณากรอกทะเบียน"
                  value={formik.values["license_plate"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={1}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  จังหวัด
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <OptionProvince
                  value={formik.values["license_province"]}
                  onChangeValue={(newValue) => {
                    formik.setFieldValue("license_province", newValue);
                  }}
                  placeholder="กรุณากรอกจังหวัด"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชนิดรถ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="car_type"
                  placeholder="กรุณากรอกชนิดรถ"
                  value={formik.values["car_type"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขตัวรถ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="car_no"
                  placeholder="กรุณากรอกเลขตัวรถ"
                  value={formik.values["car_no"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชนิดเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="engine_brand"
                  placeholder="กรุณากรอกชนิดเครื่องยนต์"
                  value={formik.values["engine_brand"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="engine_number"
                  placeholder="กรุณากรอกเลขเครื่องยนต์"
                  value={formik.values["engine_number"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>
            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลผู้รับโอน
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ชื่อผู้ซื้อ",
                    name: "buyer_name",
                    type: "text",
                    placeholder: "กรุณากรอกชื่อผู้ซื้อ",
                  }}
                  formik={formik}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  อายุ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  name="buyer_age"
                  placeholder="กรุณากรอกอายุ"
                  value={formik.values["buyer_age"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  InputProps={{ inputProps: { min: 18 } }}
                  error={
                    formik.touched.buyer_age && Boolean(formik.errors.buyer_age)
                  }
                  helperText={
                    formik.touched.buyer_age && formik.errors.buyer_age
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  สัญชาติ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={nationalities}
                  getOptionLabel={(option) => option.nationality}
                  value={
                    nationalities.find(
                      (n) => n.nationality === formik.values.buyer_nationality
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "buyer_nationality",
                      newValue?.nationality
                    );
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="กรุณากรอกสัญชาติ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.buyer_nationality &&
                        Boolean(formik.errors.buyer_nationality)
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "โทรศัพท์",
                    name: "buyer_phone",
                    type: "text",
                    placeholder: "กรุณากรอกโทรศัพท์",
                  }}
                  formik={formik}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ที่อยู่",
                    name: "buyer_address",
                    type: "text",
                    placeholder: "กรุณากรอกที่อยู่",
                  }}
                  formik={formik}
                />
              </Grid>
            </Grid>
            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลซื้อ - ขาย
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ยอดเงินทั้งหมด",
                    name: "total_price",
                    type: "number",
                    placeholder: "กรุณากรอกยอดเงินทั้งหมด",
                  }}
                  formik={formik}
                />
              </Grid>
            </Grid>
            <Typography variant="h5" sx={{ mb: 1, mb: 2 }}>
              เอกสารที่แนบ
            </Typography>
            <Grid container spacing={2} px={1}>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เอกสาร:
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <FormGroup>
                  <Grid container>
                    {Attachments.map((attachment) => (
                      <Grid item xs={6} key={attachment.id}>
                        <FormControlLabel
                          key={attachment.id}
                          control={
                            <Checkbox
                              checked={selectedAttachments.includes(
                                attachment.label
                              )}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedAttachments((prev) => [
                                    ...prev,
                                    attachment.label,
                                  ]);
                                } else {
                                  setSelectedAttachments((prev) =>
                                    prev.filter(
                                      (item) => item !== attachment.label
                                    )
                                  );
                                }
                              }}
                              name={attachment.label}
                            />
                          }
                          label={attachment.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Grid>
            </Grid>
          </Box>
        </form>
      </CustomizeDialog>
      <AlertToast
        open={error.open}
        onClose={() => setError({ open: false, status: "info", message: "" })}
        type={error.status}
        message={error.message}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </>
  );
};

const FieldRow = ({ field, formik, ...props }) => (
  <Grid container alignItems="center" spacing={1}>
    <Grid item xs={2}>
      <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
        {field.label}
      </Typography>
    </Grid>
    <Grid item xs={10}>
      {field.type === "text" || field.type === "number" ? (
        <TextField
          fullWidth
          variant="outlined"
          type={field.type}
          name={field.name}
          placeholder={field.placeholder}
          value={formik.values[field.name]}
          onChange={formik.handleChange}
          onKeyPress={(e) => {
            if (field.name.includes("phone")) {
              const charCode = e.which ? e.which : e.keyCode;
              if (charCode < 48 || charCode > 57) {
                e.preventDefault();
              }
            }
          }}
          sx={{
            "& .MuiOutlinedInput-root": { bgcolor: "white", height: "40px" },
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched[field.name] && Boolean(formik.errors[field.name])
          }
          helperText={formik.touched[field.name] && formik.errors[field.name]}
          disabled={props.disabled}
          {...props}
        />
      ) : (
        <Autocomplete
          options={nationalities}
          getOptionLabel={(option) => option.nationality}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={field.placeholder}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  height: "40px",
                },
              }}
            />
          )}
          {...props}
        />
      )}
    </Grid>
  </Grid>
);

export default ModalCreateTransferAndReceiptBuy;
